/**
 * XSM App Theme Configuration
 * This file centralizes all theme variables including colors, typography, spacing, and other UI constants
 * to maintain consistency across the application.
 */

// Colors
const colors = {
  // Primary colors
  primary: {
    main: "#0d47a1",
    light: "#1976d2",
    dark: "#20316c",
    gradient: "linear-gradient(90deg, #20316c 0%, #5d87ff 100%)",
  },

  // Background colors
  background: {
    default: "#000000",
    paper: "#272a33",
    surface: "#2c2c2e",
    card: "#20316c",
    cardDark: "#181e33",
    input: "#0f0f0f",
  },

  // Text colors
  text: {
    primary: "#ffffff",
    secondary: "#cdcdcd",
    disabled: "#5e5e5e",
  },

  // Action colors
  action: {
    active: "#1976d2",
    hover: "rgba(25, 118, 210, 0.1)",
    disabled: "#444444",
  },

  // Status colors
  status: {
    error: "#ff5252",
    success: "#4caf50",
    warning: "#ff9800",
    info: "#2196f3",
  },

  // Leaderboard specific colors
  leaderboard: {
    first: "#016393",
    second: "#325b6f",
    third: "#20343f",
    regular: "#262626",
  },

  // Border colors
  border: {
    default: "#5e5e5e",
    light: "rgba(255, 255, 255, 0.1)",
  },
};

// Typography
const typography = {
  fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",

  // Font weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },

  // Font sizes
  fontSize: {
    xs: "0.75rem", // 12px
    sm: "0.875rem", // 14px
    base: "1rem", // 16px
    md: "1.125rem", // 18px
    lg: "1.25rem", // 20px
    xl: "1.3rem", // ~21px
    "2xl": "1.5rem", // 24px
    "3xl": "1.875rem", // 30px
    "4xl": "2.25rem", // 36px
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },

  // Letter spacing
  letterSpacing: {
    tight: "-1px",
    normal: "0",
    wide: "0.3px",
    wider: "0.5px",
  },
};

// Spacing system (for margins, paddings, etc.)
const spacing = {
  xs: "0.25rem", // 4px
  sm: "0.5rem", // 8px
  md: "1rem", // 16px
  lg: "1.5rem", // 24px
  xl: "2rem", // 32px
  "2xl": "2.5rem", // 40px
  "3xl": "3rem", // 48px
  "4xl": "4rem", // 64px
};

// Borders and Radius
const borders = {
  radius: {
    xs: "3px",
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "25px",
    circle: "50%",
    pill: "50px",
  },
  width: {
    thin: "1px",
    medium: "2px",
    thick: "4px",
    extraThick: "6px",
  },
};

// Shadows
const shadows = {
  sm: "0 1px 4px rgba(0, 0, 0, 0.2)",
  md: "0 2px 8px rgba(0, 0, 0, 0.2)",
  lg: "0 4px 12px rgba(0, 0, 0, 0.3)",
  xl: "0 8px 24px rgba(0, 0, 0, 0.3)",
};

// Transitions
const transitions = {
  duration: {
    fast: "0.2s",
    medium: "0.3s",
    slow: "0.4s",
    extraSlow: "0.5s",
  },
  easing: {
    default: "ease",
    easeIn: "ease-in",
    easeOut: "ease-out",
    easeInOut: "ease-in-out",
  },
};

// Z-index
const zIndex = {
  base: 1,
  header: 100,
  modal: 500,
  tooltip: 600,
  toast: 700,
  overlay: 800,
};

// Media breakpoints (similar to those used in CSS files)
const breakpoints = {
  xs: "480px", // Extra small devices
  sm: "600px", // Small devices
  md: "768px", // Medium devices
  lg: "960px", // Large devices
  xl: "1200px", // Extra large devices
  xxl: "1600px", // Super large devices
};

// Main theme object to export
const theme = {
  colors,
  typography,
  spacing,
  borders,
  shadows,
  transitions,
  zIndex,
  breakpoints,
};

export default theme;

// Utility exports for direct imports
export {
  colors,
  typography,
  spacing,
  borders,
  shadows,
  transitions,
  zIndex,
  breakpoints,
};
